import { supabase } from '@/integrations/supabase/client';
import { enhancedSupabase } from '@/integrations/supabase/enhanced-client';
import { RpcResponse } from '@/integrations/supabase/rpc-types';
import { typedRpc } from '@/integrations/supabase/typedRpcClient';

/**
 * Default timeout values in milliseconds
 */
export const DEFAULT_TIMEOUTS = {
  STANDARD: 15000, // 15 seconds
  SHORT: 5000,     // 5 seconds
  LONG: 30000,     // 30 seconds
};

/**
 * Health check circuit breaker state
 */
interface CircuitBreakerState {
  status: 'closed' | 'open' | 'half-open';
  failures: number;
  lastCheck: number;
  lastSuccess: number;
  backoffInterval: number;
  edgeFunctionWorking: boolean;
  dbPingWorking: boolean;
}

const CIRCUIT_BREAKER = {
  MAX_FAILURES: 3,
  RESET_TIMEOUT: 30000, // 30 seconds
  state: {
    status: 'closed',
    failures: 0,
    lastCheck: 0,
    lastSuccess: 0,
    backoffInterval: 1000,
    edgeFunctionWorking: true,
    dbPingWorking: true
  } as CircuitBreakerState,
  
  // Fail the circuit if too many errors occur
  recordFailure() {
    this.state.failures += 1;
    if (this.state.failures >= this.MAX_FAILURES) {
      this.state.status = 'open';
      this.state.backoffInterval = Math.min(this.state.backoffInterval * 2, 60000); // Max 1 minute
      console.log(`Circuit breaker opened after ${this.state.failures} failures`);
      
      // Schedule circuit half-open state
      setTimeout(() => {
        console.log('Circuit breaker moving to half-open state');
        this.state.status = 'half-open';
        this.syncState();
      }, this.RESET_TIMEOUT);
    }
    this.syncState();
  },
  
  // Reset the circuit on success
  recordSuccess() {
    this.state.failures = 0;
    this.state.status = 'closed';
    this.state.lastSuccess = Date.now();
    this.state.backoffInterval = 1000; // Reset backoff
    console.log('Circuit breaker reset to closed state after successful connection');
    this.syncState();
  },
  
  // Check if the circuit is allowing requests
  isAllowingRequests() {
    // In half-open state, we allow a trial request
    if (this.state.status === 'half-open') {
      console.log('Circuit breaker in half-open state, allowing test request');
      return true;
    }
    return this.state.status !== 'open';
  },
  
  // Reset the circuit manually (useful for testing and for admin interventions)
  reset() {
    console.log('Circuit breaker manually reset');
    this.state.failures = 0;
    this.state.status = 'closed';
    this.state.lastSuccess = Date.now();
    this.state.backoffInterval = 1000;
    this.state.edgeFunctionWorking = true;
    this.state.dbPingWorking = true;
    this.syncState();
  },
  
  // Share circuit status across tabs
  syncState() {
    try {
      const currentState = JSON.stringify({
        status: this.state.status,
        failures: this.state.failures,
        lastCheck: this.state.lastCheck,
        lastSuccess: this.state.lastSuccess,
        backoffInterval: this.state.backoffInterval,
        edgeFunctionWorking: this.state.edgeFunctionWorking,
        dbPingWorking: this.state.dbPingWorking
      });
      
      // Save to session storage to maintain across page refreshes
      sessionStorage.setItem('network_circuit_state', currentState);
      
      // Use localStorage for cross-tab sync
      localStorage.setItem('network_circuit_state', currentState);
      
      // Dispatch custom event for components to listen to
      window.dispatchEvent(new CustomEvent('circuit-breaker-change', { 
        detail: { ...this.state } 
      }));
    } catch (e) {
      // Ignore storage errors
      console.warn('Error syncing circuit breaker state:', e);
    }
  },
  
  // Load state from storage (useful for page loads)
  loadState() {
    try {
      const storedState = sessionStorage.getItem('network_circuit_state');
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        // Only update specific fields that make sense to persist
        this.state.status = parsedState.status;
        this.state.failures = parsedState.failures;
        this.state.lastCheck = parsedState.lastCheck;
        this.state.lastSuccess = parsedState.lastSuccess;
        this.state.backoffInterval = parsedState.backoffInterval;
        this.state.edgeFunctionWorking = parsedState.edgeFunctionWorking;
        this.state.dbPingWorking = parsedState.dbPingWorking;
      }
    } catch (e) {
      // Ignore storage errors
      console.warn('Error loading circuit breaker state:', e);
    }
  },
  
  // Get the current state of the circuit breaker (for admin interfaces)
  getState() {
    return { ...this.state };
  }
};

// Initialize circuit breaker from storage
CIRCUIT_BREAKER.loadState();

// Listen for circuit state changes from other tabs
window.addEventListener('storage', (event) => {
  if (event.key === 'network_circuit_state') {
    CIRCUIT_BREAKER.loadState();
    
    // Dispatch event for components to react to cross-tab changes
    window.dispatchEvent(new CustomEvent('circuit-breaker-change', { 
      detail: { ...CIRCUIT_BREAKER.state } 
    }));
  }
});

/**
 * Cache for health check results to reduce connection load
 */
interface HealthCheckCache {
  result: boolean;
  timestamp: number;
  method: 'edge' | 'db' | 'navigator';
}

// Increase cache TTL from 5 seconds to 15 seconds
const HEALTH_CHECK_CACHE_TTL = 15000;
let lastHealthCheck: HealthCheckCache | null = null;

/**
 * Network status monitoring
 */
export const NetworkStatus = {
  isOnline: () => navigator.onLine,
  
  /**
   * Get the current circuit breaker state
   * This is useful for debugging and admin interfaces
   */
  getCircuitBreakerState: (): CircuitBreakerState => {
    return CIRCUIT_BREAKER.getState();
  },
  
  /**
   * Reset the circuit breaker (useful for testing or after deployments)
   */
  resetCircuitBreaker: () => {
    CIRCUIT_BREAKER.reset();
    console.log('Circuit breaker has been manually reset');
  },
  
  /**
   * Multi-level health check with caching, prioritizing Edge Function
   */
  checkConnection: async (): Promise<boolean> => {
    // Performance tracking
    const startTime = performance.now();
    
    // Check cache first with expanded TTL
    const now = Date.now();
    if (lastHealthCheck && (now - lastHealthCheck.timestamp < HEALTH_CHECK_CACHE_TTL)) {
      return lastHealthCheck.result;
    }
    
    // Always check navigator.onLine first as a quick baseline
    if (!navigator.onLine) {
      lastHealthCheck = { result: false, timestamp: now, method: 'navigator' };
      return false;
    }
    
    // Only proceed with active checks if circuit breaker allows
    if (!CIRCUIT_BREAKER.isAllowingRequests()) {
      return false;
    }
    
    CIRCUIT_BREAKER.state.lastCheck = now;
    
    try {
      let connected = false;
      
      // Step 1: Try the database ping() function first as it's more reliable
      // But set a timeout to prevent hanging
      const dbPingPromise = new Promise<boolean>(async (resolve) => {
        try {
          connected = await checkConnectionViaRPC();
          resolve(connected);
        } catch {
          resolve(false);
        }
      });
      
      // Race against a timeout
      const dbResult = await Promise.race([
        dbPingPromise,
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 3000))
      ]);
      
      if (dbResult) {
        CIRCUIT_BREAKER.recordSuccess();
        lastHealthCheck = { result: true, timestamp: now, method: 'db' };
        CIRCUIT_BREAKER.state.dbPingWorking = true;
        CIRCUIT_BREAKER.syncState();
        
        // Track performance
        const duration = Math.round(performance.now() - startTime);
        console.log(`Network check completed in ${duration}ms via DB ping`);
        
        return true;
      }
      
      // Step 2: Try lighter weight database check as fallback
      try {
        connected = await checkConnectionViaDatabase();
        
        if (connected) {
          CIRCUIT_BREAKER.recordSuccess();
          lastHealthCheck = { result: true, timestamp: now, method: 'db' };
          CIRCUIT_BREAKER.syncState();
          
          // Track performance
          const duration = Math.round(performance.now() - startTime);
          console.log(`Network check completed in ${duration}ms via DB count query`);
          
          return true;
        }
      } catch (dbError) {
        console.error('Database count query failed:', dbError);
      }
      
      // If we reach here, all checks failed
      CIRCUIT_BREAKER.recordFailure();
      lastHealthCheck = { result: false, timestamp: now, method: 'db' };
      CIRCUIT_BREAKER.syncState();
      
      // Track performance
      const duration = Math.round(performance.now() - startTime);
      console.log(`Network check failed in ${duration}ms`);
      
      return false;
    } catch (error) {
      console.error('Unexpected error in connection check:', error);
      CIRCUIT_BREAKER.recordFailure();
      lastHealthCheck = { result: false, timestamp: now, method: 'navigator' };
      CIRCUIT_BREAKER.syncState();
      return false;
    }
  }
};

/**
 * Check connection using Edge Function
 */
async function checkConnectionViaEdgeFunction(): Promise<boolean> {
  try {
    console.log('Trying to invoke ping edge function...');
    
    // Fix: Set a timeout for the request
    const timeoutPromise = new Promise<boolean>((_, reject) => {
      setTimeout(() => reject(new Error('Edge function ping timeout')), 3000);
    });
    
    const fetchPromise = supabase.functions.invoke('ping', {
      method: 'GET'
    }).then(({ data, error }) => {
      if (error) {
        console.error('Edge function ping error:', error);
        return false;
      }
      
      console.log('Edge function ping response:', data);
      return data && data.status === 'ok';
    });
    
    // Race the fetch against a timeout
    return await Promise.race([fetchPromise, timeoutPromise]);
  } catch (error) {
    console.error('Error in edge function ping:', error);
    return false;
  }
}

/**
 * Check connection using Database RPC call to ping()
 */
async function checkConnectionViaRPC(): Promise<boolean> {
  try {
    console.log('Trying to invoke ping database function...');
    
    // Use our type-safe RPC client
    const { data, error } = await typedRpc.call('ping');
    
    if (error) {
      console.error('Database ping() function error:', error);
      return false;
    }
    
    console.log('Database ping() function response:', data);
    return data && data.status === 'ok';
  } catch (error) {
    console.error('Error in database ping() function:', error);
    return false;
  }
}

/**
 * Check connection using Database (fallback method)
 */
async function checkConnectionViaDatabase(): Promise<boolean> {
  try {
    // Use the profiles table with a COUNT query which is very lightweight
    const { error } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .limit(1);
    
    return !error;
  } catch (error) {
    console.error('Error in database count query:', error);
    return false;
  }
}

/**
 * A queue of failed requests that can be retried when connection is restored
 */
class RetryQueue {
  private static instance: RetryQueue;
  private queue: Array<() => Promise<any>> = [];
  private retrying = false;
  private maxQueueSize = 10;
  private retryInProgress = false;
  private lastConnectionCheck = 0;
  private connectionCheckThrottleMs = 5000; // Don't check connection more than once every 5 seconds

  private constructor() {
    // Listen for online events
    window.addEventListener('online', () => this.processQueue());
  }

  public static getInstance(): RetryQueue {
    if (!RetryQueue.instance) {
      RetryQueue.instance = new RetryQueue();
    }
    return RetryQueue.instance;
  }

  public add(request: () => Promise<any>): void {
    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift(); // Remove oldest request if queue is full
    }
    this.queue.push(request);
    console.log(`Added request to retry queue. Queue size: ${this.queue.length}`);
    
    // Start processing queue if not already in progress
    if (!this.retrying) {
      this.scheduleQueueProcessing();
    }
  }

  private scheduleQueueProcessing(): void {
    // Use exponential backoff for retries
    const backoffMs = Math.min(calculateBackoff(CIRCUIT_BREAKER.state.failures), 30000);
    console.log(`Scheduling queue processing in ${backoffMs}ms`);
    
    setTimeout(() => this.processQueue(), backoffMs);
  }

  private async processQueue(): Promise<void> {
    if (this.retrying || this.queue.length === 0) {
      return;
    }

    this.retrying = true;
    
    // Check connection first (with throttling)
    const now = Date.now();
    let isConnected = navigator.onLine;
    
    if (now - this.lastConnectionCheck > this.connectionCheckThrottleMs) {
      this.lastConnectionCheck = now;
      isConnected = await NetworkStatus.checkConnection();
    }
    
    if (!isConnected) {
      console.log('Network still offline, delaying retry queue processing');
      this.retrying = false;
      this.scheduleQueueProcessing();
      return;
    }

    console.log(`Processing retry queue: ${this.queue.length} requests`);

    // Process oldest request first
    const request = this.queue.shift();
    if (request) {
      try {
        await request();
        console.log('Successfully processed queued request');
        
        // If successful, continue processing after a small delay
        setTimeout(() => {
          this.retrying = false;
          this.processQueue();
        }, 500);
      } catch (error) {
        console.error('Error processing queued request:', error);
        
        // Re-queue the failed request at the end if network is still available
        if (navigator.onLine) {
          this.add(request);
        }
        
        this.retrying = false;
        this.scheduleQueueProcessing();
      }
    } else {
      this.retrying = false;
    }
  }
}

export const retryQueue = RetryQueue.getInstance();

/**
 * Calculate exponential backoff time
 */
export const calculateBackoff = (attempt: number, baseMs = 1000, maxMs = 30000): number => {
  const backoff = Math.min(maxMs, baseMs * Math.pow(2, attempt));
  // Add some randomness to prevent all clients retrying at the same time
  return backoff + (Math.random() * 1000);
};

/**
 * Mark a network operation for performance tracking
 */
export const markNetworkOperation = (operationName: string) => {
  try {
    performance.mark(`network-${operationName}-start`);
    return () => {
      performance.mark(`network-${operationName}-end`);
      performance.measure(
        `network-${operationName}`,
        `network-${operationName}-start`,
        `network-${operationName}-end`
      );
    };
  } catch (e) {
    // Fallback if performance API not available
    return () => {};
  }
};
